import PhoneMask from "../../phoneMask";
import { PDF_COLORS, PDF_LINE_HEIGHT, pdfFz, divisorV2, joinComma, PDF_LABEL_FONT } from "./stylesPDFv2";

/**
 */
const lineaInline = (label, value, { alignment = "left" } = {}) => ({
  text: [
    { text: label + " ", font: PDF_LABEL_FONT, bold: true, color: PDF_COLORS.label },
    { text: value === undefined || value === null ? "" : String(value), color: PDF_COLORS.ink },
  ],
  fontSize: pdfFz(6),
  lineHeight: PDF_LINE_HEIGHT,
  alignment,
  margin: [0, 1.5, 0, 1.5],
});

/**
 * BLOQUE 0 (header) + BLOQUE 1 (metadatos emisor/documento) del sistema BIWO.
 *

 * @param {object}  params
 * @param {object}  params.empresa
 * @param {string}  params.tituloPrincipal  "Remisión" / "Factura" / ...
 * @param {string}  params.tituloFolio       "N° FTF - 116"
 * @param {boolean} params.regimenFiscal
 * @param {string|object} params.tipoComprobante 
 * @param {Array<{label,value}>} params.meta filas del bloque derecho (derecha)
 * @returns nodo pdfMake (stack con divisoria inferior)
 */
/**
 * Rejilla de campos del documento (2 columnas): cada celda muestra la etiqueta

 */
const metaGridNode = (items = [], cols = 3) => {
  const cells = (items || []).filter(Boolean);
  const cellNode = (m) => ({
    border: [false, false, false, false],
    margin: [0, 3, 8, 3],
    stack: [
      { text: String(m.label || "").replace(/:\s*$/, ""), fontSize: pdfFz(6.5), color: PDF_COLORS.muted },
      { text: m.value === undefined || m.value === null ? "" : String(m.value), fontSize: pdfFz(9), bold: true, color: PDF_COLORS.ink },
    ],
  });
  const empty = { text: "", border: [false, false, false, false] };
  const widths = cols === 3 ? ["34%", "33%", "33%"] : Array.from({ length: cols }, () => `${Math.floor(100 / cols)}%`);
  const rows = [];
  for (let i = 0; i < cells.length; i += cols) {
    const row = [];
    for (let c = 0; c < cols; c++) row.push(cells[i + c] ? cellNode(cells[i + c]) : empty);
    rows.push(row);
  }
  return {
    table: { widths, body: rows.length ? rows : [Array.from({ length: cols }, () => empty)] },
    layout: "noBorders",
  };
};

export const PDFHeaderV2 = ({
  empresa = {},
  tituloPrincipal = "",
  tituloFolio = "",
  regimenFiscal = true,
  tipoComprobante = "ingreso",
  meta = [],
  metaLayout = "list", 
} = {}) => {
  const valorComprobante = (() => {
    if (tipoComprobante && typeof tipoComprobante === "object") {
      const clave = String(tipoComprobante.clave || "").trim();
      const nombre = String(tipoComprobante.nombre || "").trim();
      return clave ? `(${clave}) ${nombre}`.trim() : nombre;
    }
    return typeof tipoComprobante === "string" && tipoComprobante.toLowerCase() === "egreso"
      ? "(E) Egreso"
      : "(I) Ingreso";
  })();

  // Dirección del emisor: una sola cadena continua separada por comas.
  const calleSeg = [empresa?.calle, empresa?.numeroCalle, empresa?.numeroInterior]
    .filter((p) => p !== undefined && p !== null && String(p).trim() !== "")
    .join(" ");
  const coloniaSeg = [empresa?.colonia?.tipoAsentamiento, empresa?.colonia?.nombre]
    .filter((p) => p !== undefined && p !== null && String(p).trim() !== "")
    .join(" ");
  const direccionTxt = joinComma([
    calleSeg,
    coloniaSeg,
    empresa?.colonia?.cp ? "C.P. " + empresa.colonia.cp : "",
    empresa?.municipio?.nombre,
    empresa?.estado?.abreviatura,
    empresa?.pais?.abreviatura,
  ]);

  const nombreEmpresa = (empresa?.nombreComercial
    ? empresa.nombreComercial
    : empresa?.persona
      ? empresa?.nombre
      : [empresa?.nombre, empresa?.apellidoPaterno, empresa?.apellidoMaterno]
        .filter((p) => p !== undefined && p !== null && String(p).trim() !== "")
        .join(" ")
  ) || "";

  const datosEmpresa = [
    ...(nombreEmpresa
      ? [{ text: nombreEmpresa, bold: true, fontSize: pdfFz(9), color: PDF_COLORS.ink, lineHeight: PDF_LINE_HEIGHT, margin: [0, 0, 0, 2] }]
      : []),
    lineaInline("R.F.C.:", empresa?.rfc || ""),
    ...(direccionTxt ? [lineaInline("Dirección:", direccionTxt)] : []),
    ...(empresa?.telefono ? [lineaInline("Teléfono:", PhoneMask(empresa.telefono))] : []),
    ...(empresa?.correo ? [lineaInline("Correo:", empresa.correo)] : []),
    ...(regimenFiscal && empresa?.regimenFiscal
      ? [lineaInline("Régimen Fiscal:", `(${empresa?.regimenFiscal?.c_RegimenFiscal}) ${empresa?.regimenFiscal?.descripcion}`)]
      : []),
    ...(tipoComprobante ? [lineaInline("Tipo de Comprobante:", valorComprobante)] : []),
  ];

 
  const metaEmpresa = (meta || [])
    .filter(Boolean)
    .map((m) => lineaInline(m.label, m.value));
  const metaMitad = Math.ceil(metaEmpresa.length / 2);
  const metaColA = metaEmpresa.slice(0, metaMitad);
  const metaColB = metaEmpresa.slice(metaMitad);

  return {
    margin: [0, 0, 0, 0],
    stack: [
      // BLOQUE 0: logo (izq) + título + folio (der). noWrap evita cortes feos.
      // Se compactó (logo y tipografías más chicos) para ganar altura y que una
      // factura de ≤5 insumos quepa en una sola hoja.
      {
        columns: [
          { width: 108, image: "logo", fit: [108, 74] },
          {
            width: "*",
            stack: [
              { text: tituloPrincipal, alignment: "right", bold: true, fontSize: 17, color: PDF_COLORS.title, noWrap: true },
              ...(tituloFolio ? [{ text: tituloFolio, alignment: "right", bold: true, fontSize: 19, color: PDF_COLORS.title, noWrap: true, margin: [0, 2, 0, 0] }] : []),
            ],
          },
        ],
        columnGap: 12,
      },

      metaLayout === "grid"
      ? { margin: [0, 14, 0, 0], stack: datosEmpresa }   // compactado para subir el bloque
      : {

          margin: [0, 10, 0, 0],
          columns: [
            { width: "40%", stack: datosEmpresa },
            metaEmpresa.length
              ? {
                  width: "60%",
                  table: {
                    widths: ["50%", "50%"],
                    body: Array.from(
                      { length: metaColA.length },
                      (_, i) => [metaColA[i], metaColB[i] || { text: "" }]
                    ),
                  },
                  layout: {
                    defaultBorder: false,
                    paddingTop: () => 0,
                    paddingBottom: () => 0,
                    paddingLeft: (i) => (i === 0 ? 0 : 14), 
                    paddingRight: () => 0,
                  },
                }
              : { width: "60%", text: "" },
          ],
          columnGap: 18,
        },
      divisorV2(PDF_COLORS.neutralDivider, 6, 1),
      // Modo "grid": franja de campos del documento a todo el ancho.
      ...(metaLayout === "grid" ? [metaGridNode(meta, 3), divisorV2(PDF_COLORS.neutralDivider, 7, 1)] : []),
    ],
  };
};

export default PDFHeaderV2;
